<template>
  <div class="layer-explode-demo">
    <div
      ref="canvasHost"
      class="canvas-host"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointerleave="onPointerUp"
      @pointercancel="onPointerUp"
    />

    <div class="ui">
      <div class="panel">
        <div class="row">
          <label for="explode-depth">Explode depth</label>
          <input
            id="explode-depth"
            v-model.number="explodeDepth"
            type="range"
            min="0"
            max="80"
            step="1"
          >
          <span class="value">{{ explodeDepth }}</span>
        </div>
        <div class="row buttons">
          <button
            type="button"
            @click="resetRotation"
          >
            Reset
          </button>
          <label class="checkbox">
            <input
              v-model="autoWobble"
              type="checkbox"
            >
            Auto-wobble
          </label>
        </div>
      </div>
      <p class="instructions">
        Click + drag to rotate. Use slider to explode layers.
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import * as THREE from 'three';

// Demo-only: keep configuration inline so this is easy to delete later.
const LAYERS = [
  'layer_01.png',
  'layer_02.png',
  'layer_03.png',
  'layer_04.png',
  'layer_05.png',
];

const canvasHost = ref(null);
const explodeDepth = ref(24);
const autoWobble = ref(true);

let scene;
let camera;
let renderer;
let group;
let planes = [];
let textures = [];
let planeAspect = 1;
let animationFrameId = 0;
let clock;

let isDragging = false;
let lastPointer = { x: 0, y: 0 };
let rotation = { x: 0, y: 0 };
let rotationVelocity = { x: 0, y: 0 };

const dragSpeed = 0.005;
const inertia = 0.92;
const returnStrength = 0.04;
const maxTilt = 0.9;
const depthScale = 0.02;

const createTextureUrls = () =>
  LAYERS.map((name) => new URL(`../assets/images/layers/${name}`, import.meta.url).href);

const loadTextures = () => {
  const loader = new THREE.TextureLoader();
  const urls = createTextureUrls();

  return Promise.all(
    urls.map(
      (url) =>
        new Promise((resolve, reject) => {
          loader.load(
            url,
            (texture) => {
              texture.colorSpace = THREE.SRGBColorSpace;
              resolve(texture);
            },
            undefined,
            (error) => reject(error)
          );
        })
    )
  );
};

const buildPlanes = (loadedTextures) => {
  textures = loadedTextures;
  const sample = textures[0];
  if (sample && sample.image) {
    planeAspect = sample.image.width / sample.image.height;
  }

  textures.forEach((texture, index) => {
    const geometry = new THREE.PlaneGeometry(planeAspect, 1);
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.renderOrder = index;
    planes.push(mesh);
    group.add(mesh);
  });
};

const updateExplodeDepth = () => {
  if (!planes.length) return;
  const spacing = explodeDepth.value * depthScale;
  const offset = (planes.length - 1) * spacing * 0.5;
  planes.forEach((plane, index) => {
    plane.position.z = index * spacing - offset;
  });
};

const updateScale = () => {
  if (!group || !camera || !canvasHost.value) return;
  const width = canvasHost.value.clientWidth;
  const height = canvasHost.value.clientHeight;
  if (!width || !height) return;

  const viewHeight =
    2 * Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5)) * camera.position.z;
  const viewWidth = viewHeight * camera.aspect;
  const scale = 0.7 * Math.min(viewWidth / planeAspect, viewHeight);
  group.scale.setScalar(scale);
};

const onResize = () => {
  if (!renderer || !camera || !canvasHost.value) return;
  const width = canvasHost.value.clientWidth;
  const height = canvasHost.value.clientHeight;
  renderer.setSize(width, height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  camera.aspect = width / height;
  camera.updateProjectionMatrix();
  updateScale();
};

const animate = () => {
  if (!renderer || !scene || !camera) return;
  const elapsed = clock.getElapsedTime();
  const wobbleX = autoWobble.value ? Math.sin(elapsed * 0.6) * 0.08 : 0;
  const wobbleY = autoWobble.value ? Math.cos(elapsed * 0.4) * 0.1 : 0;

  if (!isDragging) {
    rotationVelocity.x += (wobbleX - rotation.x) * returnStrength;
    rotationVelocity.y += (wobbleY - rotation.y) * returnStrength;
  }

  rotationVelocity.x *= inertia;
  rotationVelocity.y *= inertia;

  rotation.x = THREE.MathUtils.clamp(rotation.x + rotationVelocity.x, -maxTilt, maxTilt);
  rotation.y = THREE.MathUtils.clamp(rotation.y + rotationVelocity.y, -maxTilt, maxTilt);

  if (group) {
    group.rotation.x = rotation.x;
    group.rotation.y = rotation.y;
  }

  renderer.render(scene, camera);
  animationFrameId = requestAnimationFrame(animate);
};

const onPointerDown = (event) => {
  if (!canvasHost.value) return;
  isDragging = true;
  lastPointer = { x: event.clientX, y: event.clientY };
  canvasHost.value.setPointerCapture(event.pointerId);
};

const onPointerMove = (event) => {
  if (!isDragging) return;
  const dx = event.clientX - lastPointer.x;
  const dy = event.clientY - lastPointer.y;
  lastPointer = { x: event.clientX, y: event.clientY };

  rotation.x = THREE.MathUtils.clamp(rotation.x + dy * dragSpeed, -maxTilt, maxTilt);
  rotation.y = THREE.MathUtils.clamp(rotation.y + dx * dragSpeed, -maxTilt, maxTilt);
  rotationVelocity.x = dy * dragSpeed;
  rotationVelocity.y = dx * dragSpeed;
};

const onPointerUp = (event) => {
  if (!canvasHost.value) return;
  isDragging = false;
  if (canvasHost.value.hasPointerCapture(event.pointerId)) {
    canvasHost.value.releasePointerCapture(event.pointerId);
  }
};

const resetRotation = () => {
  rotation = { x: 0, y: 0 };
  rotationVelocity = { x: 0, y: 0 };
  if (group) {
    group.rotation.set(0, 0, 0);
  }
};

onMounted(async () => {
  const host = canvasHost.value;
  if (!host) return;

  // Demo-only: to reuse later, lift this Three.js setup into a composable that accepts
  // the host element, layer list, and spacing as options.
  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(45, host.clientWidth / host.clientHeight, 0.1, 100);
  camera.position.z = 4;

  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
  renderer.setClearColor(0x0b0b10, 1);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(host.clientWidth, host.clientHeight);
  host.appendChild(renderer.domElement);

  group = new THREE.Group();
  scene.add(group);

  clock = new THREE.Clock();

  try {
    const loadedTextures = await loadTextures();
    buildPlanes(loadedTextures);
    updateExplodeDepth();
    updateScale();
  } catch (error) {
    console.error('Failed to load layer textures for demo:', error);
  }

  window.addEventListener('resize', onResize);
  animate();
});

watch(explodeDepth, () => {
  updateExplodeDepth();
});

onBeforeUnmount(() => {
  cancelAnimationFrame(animationFrameId);
  window.removeEventListener('resize', onResize);

  if (renderer && renderer.domElement && canvasHost.value) {
    canvasHost.value.removeChild(renderer.domElement);
  }

  planes.forEach((plane) => {
    plane.geometry.dispose();
    if (plane.material.map) {
      plane.material.map.dispose();
    }
    plane.material.dispose();
  });

  textures.forEach((texture) => texture.dispose());

  if (renderer) {
    renderer.dispose();
  }

  planes = [];
  textures = [];
  group = null;
  scene = null;
  camera = null;
  renderer = null;
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;600&display=swap');

.layer-explode-demo {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: radial-gradient(circle at top, #1b1d28 0%, #0b0c12 55%, #07070b 100%);
  color: #e9edf5;
  font-family: 'Space Grotesk', 'Segoe UI', sans-serif;
}

.canvas-host {
  position: absolute;
  inset: 0;
  touch-action: none;
  cursor: grab;
}

.canvas-host:active {
  cursor: grabbing;
}

.ui {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  pointer-events: none;
  padding: 2rem;
}

.panel {
  pointer-events: auto;
  max-width: 420px;
  background: rgba(10, 12, 18, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.45);
}

.row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;
}

.row + .row {
  margin-top: 1rem;
}

.row input[type='range'] {
  flex: 1;
}

.row.buttons {
  justify-content: space-between;
}

.value {
  min-width: 2.5rem;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

button {
  background: #e9edf5;
  color: #0b0c12;
  border: none;
  border-radius: 999px;
  padding: 0.45rem 1.1rem;
  font-weight: 600;
  cursor: pointer;
}

button:hover {
  background: #f7f9ff;
}

.checkbox {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.9rem;
}

.instructions {
  margin: 0;
  font-size: 0.95rem;
  color: rgba(233, 237, 245, 0.75);
  letter-spacing: 0.01em;
}

@media (max-width: 720px) {
  .ui {
    padding: 1.25rem;
  }

  .panel {
    width: 100%;
  }

  .row {
    flex-wrap: wrap;
  }
}
</style>
